import _, { set } from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Controller, FormProvider, useFieldArray, useForm, useFormContext } from "react-hook-form";
import { BackButton, Button } from "../../../components/Button";
import { useDispatch, useSelector } from "react-redux";
import { selectServiceAccount } from "../../../store/models/DApp";
import Video from "../../../store/models/Video";
import { useDropzone } from 'react-dropzone';
import { fileNameFromUrl, isValidUrl, isVideoPlatformUrl, mergeUrl } from "../../../utils/url";
import { ModalTypes, SupportedNetworks, Urls } from '../../../constants';
import PageTitleBar from "../../../components/PageTitleBar";
import Select from 'react-select'
import { CgClose, CgInfo } from "react-icons/cg";
import cx from 'classnames';
import EnterpriseBadge from '../../../assets/icons/badge-enterprise.svg';
import Tooltip from "../../../components/Tooltip";
import iconEthereum from '../../../assets/icons/blockchains/icon-ethereum.png';
import iconPolygon from '../../../assets/icons/blockchains/icon-polygon.png';
import iconTheta from '../../../assets/icons/blockchains/icon-theta.png';
import iconPog from '../../../assets/icons/blockchains/icon-pogs.png';
import iconGrove from '../../../assets/icons/blockchains/icon-grove.png';
import iconPassaways from '../../../assets/icons/blockchains/icon-plasm.png';
import { selectCurrentProject } from "../../../store/models/Project";
import { selectCurrentUserId } from '../../../store/models/User';
import UIState from '../../../store/UIState';
import { TextInput } from "../../../components/form/TextInput";
import { VideoInput } from "../../../components/form/VideoInput";
import { DownChevronIcon } from "../../../components/Svg";
import { toast } from "react-toastify";
import { pushTo } from "../../../store/history";
import { useHistory } from "react-router-dom";
import Ai, { selectAllQuotas } from '../../../store/models/Ai';
import Organization, { selectCurrentOrgId } from '../../../store/models/Organization';
import { formatDuration } from '../../../utils';


const FileInput = props => {
  const { name, label = name } = props
  const { register, unregister, setValue, watch } = useFormContext()
  const files = watch(name)
  const onDrop = useCallback(
    droppedFiles => {
      setValue(name, droppedFiles, { shouldValidate: true })
    },
    [setValue, name]
  )
  const { getRootProps, getInputProps, isDragActive, isDragAccept, isDragReject, acceptedFiles } = useDropzone({
    onDrop,
    accept: props.accept,
  });
  const style = useMemo(() => ({
    ...baseStyle,
    ...(isDragActive ? activeStyle : {}),
    ...(isDragAccept ? acceptStyle : {}),
    ...(isDragReject ? rejectStyle : {})
  }), [
    isDragActive,
    isDragReject,
    isDragAccept
  ]);
  const acceptedFileItems = acceptedFiles.map(file => (
    <div key={file.path}
      style={{
        color: '#18C99D'
      }}
    >
      {file.path}
    </div>
  ));

  useEffect(() => {
    register(name)
    return () => {
      unregister(name)
    }
  }, [register, unregister, name])
  return (
    <>
      <div className="container">
        <div {...getRootProps({ style })}>
          <input {...props} {...getInputProps()} />
          {!_.isEmpty(acceptedFileItems)
            ? acceptedFileItems
            : <p>Drag & drop or <span className={"choose-file-text"}>Choose a file</span> to upload</p>
          }
        </div>
      </div>
    </>
  )
}

async function uploadFile(url, file) {
  return fetch(url, {
    method: 'PUT',
    body: file,
    headers: {
      "Content-Type": 'application/octet-stream'
    }
  });
}

const baseStyle = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: '30px',
  borderWidth: 2,
  fontWeight: 600,
  fontSize: 15,
  borderRadius: 14,
  cursor: 'pointer',
  borderColor: '#282B3B',
  borderStyle: 'dashed',
  backgroundColor: 'transparent',
  color: '#636B91',
  marginBottom: 45,
  outline: 'none',
  transition: 'border .24s ease-in-out'
};

const activeStyle = {
  borderColor: '#18C99D'
};

const acceptStyle = {
  borderColor: '#18C99D'
};

const rejectStyle = {
  borderColor: '#ff1744'
};


function CreateVideoForm({ onSubmit, loading, isEnterpriseAccount, videoLengthLimit }) {
  const formMethods = useForm({
    defaultValues: {
      resolutions: resolutionOptions.map(o => o.value),
      video_type: 0,
      use_studio_drm: false,
      chain_id: 361,
      drm_rules: [{ nft_collection: "", chain_id: 361 }]
    }
  });
  const { register, handleSubmit, control, getValues, setValue, formState: { error, touchedFields } } = formMethods;
  const { fields, append, remove } = useFieldArray({
    control, // control props comes from useForm (optional: if you are using FormContext)
    name: "drm_rules", // unique name for your Field Array
  });

  const [rerenderFlag, setRerenderFlag] = useState(false);
  const [hasNftDrm, setHasNftDrm] = useState(false);
  const videoType = formMethods.getValues().video_type;
  const useStudioDrm = formMethods.getValues().use_studio_drm;

  const onChangeValue = (name, value) => {
    setValue(name, value);
    setRerenderFlag(prevFlag => !prevFlag);
  }

  return (
    <FormProvider {...formMethods}>
      <form className={'Form CreateVideoForm'}>
        <div className={'CreateVideoForm__label'}>
          Video source
          {` (*Up to ${formatDuration(videoLengthLimit)} videos supported under your current plan.)`}
        </div>
        <input type="text"
          className='CreateVideoForm__default-input'
          placeholder="Enter video url"
          {...register("source_uri", { required: false })} />
        <input type="hidden" {...register("playback_policy", { required: true, value: 'public' })} />

        <div className={'CreateVideoForm__or'}>OR</div>

        <FileInput accept="video/*"
          name={'file_to_upload'}
          refs={register("file_to_upload", { required: false })}
        />

        <div className={'CreateVideoForm__label'}>Video name</div>
        <input className='CreateVideoForm__default-input' type="text"
          placeholder="Enter video name" {...register("metadata.filename", { required: false })} />

        <div className='CreateVideoForm__selects'>
          <div className='CreateVideoForm__select'>
            <div className='CreateVideoForm__label'>Resolutions *</div>
            <div className='CreateVideoForm__select--wrap'>
              <Controller
                name="resolutions"
                control={control}
                render={({ field: { onChange } }) => (
                  <Select
                    className={'CreateVideoForm__select--resolution'}
                    defaultValue={resolutionOptions}
                    onChange={(options) =>
                      onChange(options?.map((option) => option.value))
                    }
                    styles={{ ...selectStyles }}
                    options={resolutionOptions}
                    isMulti
                    isClearable={false}
                    placeholder={'Select resolutions'}
                    isSearchable={false}
                  />
                )}
              />
            </div>
          </div>
          <div className='CreateVideoForm__select'>
            <div className='CreateVideoForm__label'>
              Elite Edge Node Workers *
              <Tooltip tooltip={"By default, videos are processed by community ran Elite Edge Nodes."} asChild={true}>
                <div className={"icon-info"}><CgInfo /></div>
              </Tooltip>
            </div>

            <input type="hidden" {...register('video_type')} />
            <div className='CreateVideoForm__encode-options'>
              <div className={cx('CreateVideoForm__encode-option left', { active: videoType === 1 })}
                onClick={() => onChangeValue('video_type', encodeOptions[0].value)}>{encodeOptions[0].label}</div>
              <div className={cx('CreateVideoForm__encode-option right', { active: videoType === 0 })}
                onClick={() => onChangeValue('video_type', encodeOptions[1].value)}>{encodeOptions[1].label}</div>
            </div>

            <div className='CreateVideoForm__select--note'>Internal workers are recommended<br />for videos longer than 5
              minutes
            </div>
          </div>
        </div>

        <div className={'CreateVideoForm__drm'}>
          <div className={'CreateVideoForm__drm--enable-drm'}>
            <div className={'CreateVideoForm__drm--enable-drm--toggle-wrapper'}>
              <input className={"CreateVideoForm__drm--enable-drm--toggle"} type="checkbox" readOnly
                checked={hasNftDrm} />
              <div className={"CreateVideoForm__drm--enable-drm--slider"} onClick={() => setHasNftDrm(!hasNftDrm)} />

            </div>
            <div className={'CreateVideoForm__drm--enable-drm--texts'}>
              <div className={'CreateVideoForm__drm--enable-drm--title'}>Enable NFT based DRM</div>
              <div className={'CreateVideoForm__drm--enable-drm--description'}>{hasNftDrm
                ? "Select the NFTs that will allow access to your video"
                : "Protect your videos by requiring users to have specific NFT(s)"}
              </div>
            </div>
          </div>

          <div className={cx("drm-details", { hidden: !hasNftDrm })}>
            <input type="hidden" {...register('video_type')} />
            <div className='CreateVideoForm__use-studio-drm'>
              <div className={cx('CreateVideoForm__use-studio-drm--option left', { active: !useStudioDrm })}
                onClick={() => onChangeValue('use_studio_drm', false)}>
                <div className={"CreateVideoForm__use-studio-drm--option--title"}>Standard DRM</div>
                <div className={"CreateVideoForm__use-studio-drm--option--desc"}>Essential protection for digital
                  content,<br />perfect for creators and small businesses.
                </div>
              </div>
              <Tooltip tooltip={"Enterprise Plan Required"} hidden={isEnterpriseAccount}>
                <div className={cx('CreateVideoForm__use-studio-drm--option right', {
                  active: useStudioDrm && isEnterpriseAccount,
                  disabled: !isEnterpriseAccount
                })}
                  onClick={() => isEnterpriseAccount && onChangeValue('use_studio_drm', true)}>
                  <div className={"CreateVideoForm__use-studio-drm--option--badge"}><img src={EnterpriseBadge}
                    alt={"enterprise-badge"} />
                  </div>
                  <div className={"CreateVideoForm__use-studio-drm--option--title"}>Studio-Level DRM</div>
                  <div className={"CreateVideoForm__use-studio-drm--option--desc"}>Advanced security for high-value
                    content<br />like movies and TV.
                  </div>
                </div>
              </Tooltip>
            </div>

            <div className={'CreateVideoForm__nft-collection-title'}>NFT Collections</div>
            <div className={'CreateVideoForm__nft-collection-desc'}>Select the NFT collections that will allow access to
              your video
            </div>

            {fields.map((field, index) => {
              return <section key={field.id} className="CreateVideoForm__section">
                <div className='CreateVideoForm__drm-left'>
                  <div className='CreateVideoForm__select--wrap'>
                    <Controller
                      name={`drm_rules.${index}.chain_id`}
                      control={control}
                      render={({ field: { onChange } }) => (
                        <Select
                          className='CreateVideoForm__select--resolution'
                          defaultValue={SupportedNetworks[0]}
                          onChange={(option) => onChange(option.value)}
                          styles={selectStyles}
                          options={SupportedNetworks}
                          getOptionLabel={(option) => <CustomOption data={option} />}
                          placeholder={'Select NFT network'}
                          isSearchable={false}
                        />
                      )}
                    />
                  </div>
                </div>
                <div className={'CreateVideoForm__drm-right'}>
                  <div className='CreateVideoForm__drm-address'>
                    <input type="text"
                      className='CreateVideoForm__drm-address--input '
                      placeholder="Collection Address"
                      {...register(`drm_rules.${index}.nft_collection`, { required: false })} />

                    <CgClose className='CreateVideoForm__drm-address--close-btn' onClick={() => remove(index)}
                      color='grey' />
                  </div>
                  <div className='CreateVideoForm__drm-address'>
                    <input type="text"
                      className='CreateVideoForm__drm-address--input '
                      placeholder="Collection Name"
                      {...register(`drm_rules.${index}.name`, { required: false })} />
                  </div>

                  <div className='CreateVideoForm__drm-address'>
                    <input type="text"
                      className='CreateVideoForm__drm-address--input '
                      placeholder="Collection Thumbnail URL"
                      {...register(`drm_rules.${index}.image`, { required: false })} />
                  </div>

                  <div className='CreateVideoForm__drm-address'>
                    <input type="text"
                      className='CreateVideoForm__drm-address--input '
                      placeholder="Collection Link"
                      {...register(`drm_rules.${index}.link`, { required: false })} />
                  </div>
                </div>

              </section>
            })}
            <Button role={'button'}
              className={"CreateVideoForm__nft-collection--add"}
              onClick={() => append({ nft_collection: "", chain_id: 361 })}
              title='Add another collection' />
          </div>
        </div>

        <Button title={'Save'}
          role={'submit'}
          loading={loading}
          style={{
            marginTop: 30,
            width: 200,
            height: 40,
            marginLeft: 'auto',
            marginRight: 'auto',
          }}
          onClick={formMethods.handleSubmit(onSubmit)}
        />
      </form>
    </FormProvider>
  );
}

function CreateVideoFormSplit({ onSubmit, videoApiId, isEnterpriseAccount, videoLengthLimit }) {
  const dispatch = useDispatch()
  const history = useHistory()
  const [parts, setParts] = useState(2);
  const [values, setValues] = useState({
    file_name: "",
    resolutions: [1080, 720, 360],
    playback_policy: "public",
    video_type: 3,
    worker: "external"
  })
  const [videoUploads, setVideoUploads] = useState({})
  const [uploadIds, setUploadIds] = useState()
  const [selectedResolution, setSelectedResolution] = useState([])
  const [useUrlIndex, setUseUrlIndex] = useState(Array(2).fill(false))
  const [showRes, setShowRes] = useState(false)
  const [useUrl, setUseUrl] = useState(false)

  const [warning, setWarning] = useState(null)
  const [loading, setLoading] = useState(false)
  const [state, setState] = useState(null)

  const onIncreasePart = () => {
    setParts(parts + 1)
    setUseUrlIndex(Array(parts + 1).fill(false))
  }
  const onDecreasePart = () => {
    setParts(parts - 1)
    setUseUrlIndex(Array(parts - 1).fill(false))
  }

  const onInputChange = (value, name) => {
    setValues({
      ...values,
      [name]: value
    })
  }

  const allResolutions = [2160, 1080, 720, 360]

  const removeRes = (res) => {
    const newResolutions = _.without(values.resolutions, res)
    setValues({ ...values, resolutions: newResolutions })
  }

  const addRes = async (res) => {
    await setValues((values => ({
      ...values,
      resolutions: [...values?.resolutions, res]
    })))
  }

  const availableRes = _.filter(allResolutions, (r) => !_.includes(values.resolutions, r))



  const onUrlToggleIndv = (index) => { //for future support of some parts url some parts upload
    let newIndexStates = [...useUrlIndex]
    newIndexStates[index] = !newIndexStates[index]
    setUseUrlIndex(newIndexStates)
  }

  const onAddVideoUpload = (value, name) => {
    setVideoUploads({
      ...videoUploads,
      [name]: value
    })
  }

  const onToggleUrl = () => {
    setUseUrl(!useUrl)
  }

  const uploadDivs = Array.from({ length: parts }, (_, index) => (

    <div key={index} className='upload-item'>
      <div className='upload-item-text'>
        Video Source {index + 1}
        {` (*Up to ${formatDuration(videoLengthLimit)} videos supported under your current plan.)`}
      </div>
      <div className='upload-control'>
        {useUrl
          ? <TextInput
            name={'video_source' + index}
            value={videoUploads?.video_source + index || null}
            placeholder={'Enter Video URL'}
            onChange={(e) => onAddVideoUpload(e.target.value, e.target.name)} />

          : <VideoInput
            label={'Upload Video' + index}
            name={'video_object' + index}
            value={videoUploads?.video_object + index}
            onChange={onAddVideoUpload} />
        }
      </div>
    </div>
  )
  )

  const onClickWorker = (worker) => {
    setValues({
      ...values,
      worker: worker
    })
  }

  const onResControl = () => {
    setShowRes(!showRes)
  }

  const onUploadVideo = async () => {
    setLoading(true)

    try {
      if (useUrl) {
        const urls = _
          .chain(videoUploads)
          .pickBy((v, k) => k.startsWith('video_source'))
          .values()
          .value();

        let video = await dispatch(Video.actions.createCombinedVideo(values, { source_uris: urls }));
        let videoId = _.get(video.body.videos[0], 'id')
        pushTo(mergeUrl(Urls.VIDEO_VIEW_VIDEO, { videoId }));
      }

      else {
        setState('Uploading video...');
        setWarning('Do not close this tab or browser');

        const uploadPromises = _.map(videoUploads, async (videoUpload) => {
          const { id, presigned_url } = await dispatch(
            Video.actions.createVideoUploadUrl(videoApiId)
          );
          await uploadFile(presigned_url, videoUpload);
          return id;
        });
        const uploadIds = await Promise.all(uploadPromises);
        let video = await dispatch(Video.actions.createCombinedVideo(values, { source_upload_ids: uploadIds }));
        let videoId = _.get(video.body.videos[0], 'id')
        pushTo(mergeUrl(Urls.VIDEO_VIEW_VIDEO, { videoId }));
      }

    } catch (e) {
      toast.error(e.message)
    }
  }

  return (
    <div className='create-video-form-split'>
      <div className='top-section'>
        <div className='select-parts'>Upload <Button title={'-'} disabled={parts === 2} onClick={onDecreasePart} /> <span className='part'> {parts} </span> <Button title={'+'} onClick={onIncreasePart} /> video files</div>
      </div>

      <div className='upload-section'>
        <div className='video-name-wrap'>
          <TextInput label='Video Name'
            name='file_name'
            value={values?.file_name}
            placeholder={'Enter video name'}
            onChange={(e) => onInputChange(e.target.value, e.target.name)}

          />

          <div className={'toggle-wrapper'}>
            <input className={"toggle"} type="checkbox" readOnly
              checked={useUrl} />
            <div className={"slider"} onClick={onToggleUrl} />
            <div className='toggle-text'>Use URL</div>
          </div>
        </div>

        {uploadDivs}

        <div className='upload-settings'>

          <div className='resolution-wrap'>
            <div className='resolution-text'> Resolutions </div>
            <div className={showRes ? 'resolution-control clicked' : 'resolution-control'}>
              <div className='resolution'>
                {_.isEmpty(values?.resolutions) &&
                  <div className='empty'>Select a resolution</div>
                }
                {_.map(values?.resolutions, (res, id) => (
                  <div className='res' key={id} onClick={() => removeRes(res)}> {res}P <span> x </span></div>
                ))}
              </div>
              <div className='carat-icon' onClick={onResControl}> <DownChevronIcon className={showRes ? 'selected' : ''} /> </div>
            </div>
            {showRes &&
              <div className='available-resolutions'>
                {_.isEmpty(availableRes)
                  ? <div className='empty'>No resolutions</div>

                  : _.map(availableRes, (res, id) => (
                    <div className='select-resolution' key={id} onClick={() => addRes(res)}> {res}P </div>
                  ))

                }

              </div>
            }
          </div>
          { /*
          <div className='workers-wrap'>
            <div className='workers-text'>Elite Edge Node Workers <span>
              <Tooltip tooltip={"By default, videos are processed by community ran Elite Edge Nodes."} asChild={true}>
                <div className={"icon-info"}><CgInfo/></div>
              </Tooltip>
            </span></div>
            <div className='workers-control'>
              <div className={values?.worker === 'internal' ? 'worker left selected' : 'worker left'} onClick={() => onClickWorker('internal')}>INTERNAL</div>
              <div className={values?.worker === 'external' ? 'worker right selected' : 'worker right'} onClick={() => onClickWorker('external')}>EXTERNAL</div>
            </div>
            <div className='workers-disclaimer'>Internal workers are recommended for videos longer than 5 minutes.</div>
          </div> */ }

        </div>
      </div>

      <div className='bottom-section'>
        <div className='drm-wrap'>
          <div className='drm'></div>
        </div>

        <div className='button-wrap'>
          <Button title={'Upload'} onClick={onUploadVideo} loading={loading} className='upload-button' />
          {
            state &&
            <div className={'CreateVideoPage__state'}>
              {state}
            </div>
          }

          {
            warning &&
            <div className={'CreateVideoPage__warning'}>
              {warning}
            </div>
          }
        </div>


      </div>
    </div>
  )

}

function CreateVideoPage() {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [state, setState] = useState(null);
  const [warning, setWarning] = useState(null);
  const [error, setError] = useState(null);
  const project = useSelector(state => selectCurrentProject(state));
  const userId = useSelector(state => selectCurrentUserId(state));
  const videoApiId = project?.tva_id
  const serviceAccount = useSelector(state => selectServiceAccount(state, videoApiId));
  const isEnterpriseAccount = serviceAccount?.max_studio_drm_streams > 0;
  const [splitUpload, setSplitUpload] = useState(false);
  const quotas = useSelector(state => selectAllQuotas(state));
  const orgId = useSelector(state => selectCurrentOrgId(state));
  const [isBasicPlan, setIsBasicPlan] = useState(true);
  const [videoLengthLimit, setVideoLengthLimit] = useState(0);

  useEffect(() => {
    (async () => {
      if (orgId) {
        try {
          let usageRes = await dispatch(Organization.actions.fetchOrgChatbotQuotaUsage(orgId));
          setVideoLengthLimit(_.get(usageRes, 'video_length_limit'));
        } catch (e) {
          console.log('error:', e.message);
        }
      }
    })();
  }, [orgId]);

  const onSubmit = async (data) => {
    if (_.isNil(userId)) {
      dispatch(UIState.actions.showModal(ModalTypes.SIGNUP));
      return;
    }
    let videoData = data;

    if (_.isNil(data.file_to_upload)) {
      if (!isValidUrl(videoData.source_uri)) {
        setError('Invalid video URL. Please fix and then try again.');
        return;
      }
      if (isVideoPlatformUrl(videoData.source_uri)) {
        setError('Invalid video URL. You must use the raw video URL (i.e. a downloadable video)');
        return;
      }
    }

    setLoading(true);
    setError(null);

    try {
      if (data.file_to_upload) {
        setState('Uploading video...');
        setWarning('Do not close this tab or browser');
        const file = data.file_to_upload[0];
        const upload = await dispatch(Video.actions.createVideoUploadUrl(videoApiId));
        await uploadFile(upload.presigned_url, file);


        videoData = _.pick(data, ['playback_policy', 'resolutions', 'video_type', 'metadata', 'use_studio_drm']);
        videoData = {
          ...videoData,
          source_upload_id: upload.id,
          file_name: file.name
        }
      } else {
        videoData = _.pick(data, ['source_uri', 'playback_policy', 'resolutions', 'video_type', 'metadata', 'use_studio_drm']);
        videoData = {
          ...videoData,
          file_name: fileNameFromUrl(data.source_uri)
        }
      }

      if (data.drm_rules) {
        let collections = data.drm_rules
          .filter(obj => obj.nft_collection !== '')
          .map(obj => ({
            nft_collection: obj.nft_collection.trim(),
            chain_id: parseInt(obj.chain_id) || 361,
            title: obj.name,
            image: obj.image,
            link: obj.link
          }))
        if (collections.length > 0) {
          videoData['drm_rules'] = collections;
          videoData['use_drm'] = true;
        }
      }

      setState('Creating video...');
      setWarning(null);

      const video = await dispatch(Video.actions.createVideo(videoData));
    } catch (e) {
      setLoading(false);
      setState(null);
      setError(e.message || 'Ooops');
    }
  };

  return (
    <div className={'CreateVideoPage'}>
      <div className={'CreateVideoPage__content'}>
        <PageTitleBar title={'New Video'}
          leftContent={<BackButton title={'Videos'}
            href={mergeUrl(Urls.DASHBOARD_VIDEO)} />}
          rightContent={
            <div className={'toggle-wrapper'}>
              <input className={"toggle"} type="checkbox" readOnly
                checked={splitUpload} />
              <div className={"slider"} onClick={() => setSplitUpload(!splitUpload)} />
              <div className='toggle-text'>Split Uploads</div>
            </div>
          }
        />
        {!splitUpload &&
          <CreateVideoForm onSubmit={onSubmit}
            loading={loading}
            videoApiId={videoApiId}
            isEnterpriseAccount={isEnterpriseAccount}
            videoLengthLimit={videoLengthLimit}
          />}

        {splitUpload &&
          <CreateVideoFormSplit onSubmit={onSubmit}
            loading={loading}
            videoApiId={videoApiId}
            isEnterpriseAccount={isEnterpriseAccount}
            videoLengthLimit={videoLengthLimit}
          />
        }
        {
          state &&
          <div className={'CreateVideoPage__state'}>
            {state}
          </div>
        }
        {
          warning &&
          <div className={'CreateVideoPage__warning'}>
            {warning}
          </div>
        }
        {
          error &&
          <div className={'CreateVideoPage__error'}>
            {error}
          </div>
        }
      </div>
    </div>
  );
}

const selectStyles = {
  control: styles => ({
    ...styles,
    backgroundColor: 'transparent',
    borderColor: '#191D29',
    height: 40,
    borderRadius: 6,

  }),
  menu: (styles, state) => ({
    ...styles,
    border: 'none',
    backgroundColor: '#191D29',
  }),
  option: (styles, { data, isDisabled, isFocused, isSelected }) => {
    return {
      ...styles,
      backgroundColor: '#191D29',
      borderColor: '#2C2F40',
      color: ((isFocused || isSelected) ? '#18C99D' : '#8A8FB5'),
      cursor: 'pointer',
      fontSize: 14
    };
  },
  dropdownIndicator: (styles, state) => ({
    ...styles,
    color: '#636B91',
  }),
  indicatorSeparator: (styles, state) => ({
    ...styles,
    color: '#8A8FB5',
    backgroundColor: '#8A8FB5',
    display: 'none'
  }),
  input: styles => ({ fontSize: 14, ...styles }),
  placeholder: styles => ({ ...styles, color: '#636B91' }),
  singleValue: (styles, { data }) => ({ ...styles, fontWeight: 500, fontSize: 14, color: '#636B91' }),
  multiValue: styles => ({
    ...styles, padding: 1, borderRadius: 4, backgroundColor: "rgba(24, 201, 157, 0.10)", color: '#18C99D',
    ':hover': {
      backgroundColor: 'rgba(24, 201, 157, 0.25)',
    }
  }),
  multiValueLabel: styles => ({ ...styles, color: '#18C99D', fontWeight: 600 }),
  multiValueRemove: (styles, { isFocused }) => ({
    ...styles,
    ':hover': {
      backgroundColor: 'rgba(24, 201, 157, 0)',
    },
  }),
  valueContainer: styles => ({ ...styles, paddingRight: 0, border: 'none' })
};

export const CustomOption = ({ data }) => (
  <div className={"select-custom-option"}>
    <img src={getBlockchainLogo(data.value)} alt={data.label} style={{ width: 24, marginRight: 10 }} />
    {data.label}
  </div>
);

const encodeOptions = [{
  label: 'Internal',
  value: 1
},
{
  label: 'External',
  value: 0
}]
const resolutionOptions = [
  { label: '2160P', value: 2160 },
  { label: '1080P', value: 1080 },
  { label: '720P', value: 720 },
  { label: '360P', value: 360 },
]

export const getBlockchainLogo = (chainId) => {
  switch (parseInt(chainId)) {
    case 1:
    case 5:
      return iconEthereum
    case 9065:
      return iconPog
    case 137:
      return iconPolygon
    case 47683:
      return iconGrove
    case 7734:
      return iconPassaways
    default:
      return iconTheta
  }
}

export default CreateVideoPage;
